import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CalendarMonthRounded as CalendarIcon,
  ChevronLeftRounded as NextIcon,
  ChevronRightRounded as PreviousIcon,
  CloseRounded as ClearIcon
} from '@mui/icons-material';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isBefore,
  isSameDay,
  startOfDay,
  startOfMonth,
  subMonths
} from 'date-fns-jalali';

const WEEKDAYS = [
  { short: 'ش', long: 'شنبه' },
  { short: 'ی', long: 'یکشنبه' },
  { short: 'د', long: 'دوشنبه' },
  { short: 'س', long: 'سه شنبه' },
  { short: 'چ', long: 'چهارشنبه' },
  { short: 'پ', long: 'پنجشنبه' },
  { short: 'ج', long: 'جمعه' }
];
const WEEKDAY_NAMES_BY_JS_DAY = [
  'یکشنبه',
  'دوشنبه',
  'سه شنبه',
  'چهارشنبه',
  'پنجشنبه',
  'جمعه',
  'شنبه'
];
const PERSIAN_MONTHS = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند'
];
const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const CALENDAR_WIDTH = 304;
const CALENDAR_HEIGHT = 350;

function toPersianDigits(value) {
  return String(value).replace(/\d/g, (digit) => PERSIAN_DIGITS[Number(digit)]);
}

function isoToDate(value) {
  if (!value) return null;
  const [year, month, day] = String(value).split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day, 12);
  return Number.isNaN(date.getTime()) ? null : date;
}

function dateToIso(date) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function jalaliParts(date) {
  const [year, month, day] = format(date, 'yyyy/M/d').split('/').map(Number);
  return { year, month, day };
}

function jalaliDateLabel(date) {
  const { year, month, day } = jalaliParts(date);
  return toPersianDigits(
    `${year}/${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`
  );
}

function jalaliMonthLabel(date) {
  const { year, month } = jalaliParts(date);
  return `${PERSIAN_MONTHS[month - 1]} ${toPersianDigits(year)}`;
}

function jalaliAccessibleLabel(date) {
  const { year, month, day } = jalaliParts(date);
  return `${WEEKDAY_NAMES_BY_JS_DAY[date.getDay()]} ${toPersianDigits(day)} ${PERSIAN_MONTHS[month - 1]} ${toPersianDigits(year)}`;
}

function calendarPosition(trigger) {
  const rect = trigger.getBoundingClientRect();
  const availableWidth = Math.min(CALENDAR_WIDTH, Math.max(0, window.innerWidth - 16));
  const availableHeight = Math.min(CALENDAR_HEIGHT, Math.max(0, window.innerHeight - 16));
  const left = Math.max(8, Math.min(
    window.innerWidth - availableWidth - 8,
    rect.right - availableWidth
  ));
  const belowTop = rect.bottom + 6;
  const aboveTop = rect.top - availableHeight - 6;
  const top = belowTop + availableHeight <= window.innerHeight - 8
    ? belowTop
    : (aboveTop >= 8 ? aboveTop : Math.max(8, window.innerHeight - availableHeight - 8));
  return {
    left,
    top
  };
}

export default function SolarHijriDatePicker({
  value,
  onChange,
  min,
  ariaLabel = 'انتخاب تاریخ شمسی',
  placeholder = 'انتخاب تاریخ'
}) {
  const selectedDate = isoToDate(value);
  const minimumDate = isoToDate(min);
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(selectedDate || minimumDate || new Date());
  const [position, setPosition] = useState({ left: 12, top: 12 });
  const triggerRef = useRef(null);
  const calendarRef = useRef(null);

  useEffect(() => {
    const nextSelectedDate = isoToDate(value);
    if (nextSelectedDate) setViewMonth(nextSelectedDate);
  }, [value]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const updatePosition = () => {
      if (triggerRef.current) setPosition(calendarPosition(triggerRef.current));
    };
    const closeOnOutsideClick = (event) => {
      if (
        !triggerRef.current?.contains(event.target)
        && !calendarRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    updatePosition();
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  const monthDays = useMemo(() => {
    const monthStart = startOfMonth(viewMonth);
    const leadingEmptyDays = (monthStart.getDay() + 1) % 7;
    const days = eachDayOfInterval({ start: monthStart, end: endOfMonth(viewMonth) });
    const cells = [...Array(leadingEmptyDays).fill(null), ...days];
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [viewMonth]);

  const previousMonth = subMonths(viewMonth, 1);
  const previousDisabled = minimumDate
    ? isBefore(endOfMonth(previousMonth), startOfDay(minimumDate))
    : false;

  const selectDate = (date) => {
    onChange(dateToIso(date));
    setIsOpen(false);
  };

  const calendar = isOpen && typeof document !== 'undefined' ? createPortal(
    <div
      ref={calendarRef}
      className="dev-jalali-calendar"
      style={{ left: position.left, top: position.top }}
      role="dialog"
      aria-label="تقویم هجری شمسی"
      dir="rtl"
    >
      <div className="dev-jalali-calendar-head">
        <button
          type="button"
          className="dev-jalali-nav"
          onClick={() => setViewMonth(previousMonth)}
          disabled={previousDisabled}
          title="ماه قبل"
          aria-label="ماه قبل"
        >
          <PreviousIcon fontSize="small" />
        </button>
        <strong>{jalaliMonthLabel(viewMonth)}</strong>
        <button
          type="button"
          className="dev-jalali-nav"
          onClick={() => setViewMonth(addMonths(viewMonth, 1))}
          title="ماه بعد"
          aria-label="ماه بعد"
        >
          <NextIcon fontSize="small" />
        </button>
      </div>
      <div className="dev-jalali-weekdays" role="row">
        {WEEKDAYS.map((weekday) => (
          <span key={weekday.long} title={weekday.long} role="columnheader">{weekday.short}</span>
        ))}
      </div>
      <div className="dev-jalali-days" role="grid">
        {monthDays.map((date, index) => {
          if (!date) return <span className="dev-jalali-day empty" key={`empty-${index}`} />;
          const disabled = minimumDate && isBefore(startOfDay(date), startOfDay(minimumDate));
          const selected = selectedDate && isSameDay(date, selectedDate);
          const today = isSameDay(date, new Date());
          return (
            <button
              type="button"
              className={`dev-jalali-day${selected ? ' selected' : ''}${today ? ' today' : ''}`}
              key={date.toISOString()}
              disabled={disabled}
              onClick={() => selectDate(date)}
              aria-label={jalaliAccessibleLabel(date)}
              aria-selected={Boolean(selected)}
              role="gridcell"
            >
              {toPersianDigits(jalaliParts(date).day)}
            </button>
          );
        })}
      </div>
      <div className="dev-jalali-calendar-foot">
        <button
          type="button"
          onClick={() => {
            const today = new Date();
            if (!minimumDate || !isBefore(startOfDay(today), startOfDay(minimumDate))) selectDate(today);
          }}
          disabled={minimumDate && isBefore(startOfDay(new Date()), startOfDay(minimumDate))}
        >
          امروز
        </button>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="dev-jalali-date-field" ref={triggerRef}>
      <button
        type="button"
        className={`dev-jalali-date-trigger${isOpen ? ' open' : ''}`}
        onClick={() => {
          if (!isOpen) setViewMonth(selectedDate || minimumDate || new Date());
          setIsOpen((open) => !open);
        }}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <CalendarIcon fontSize="small" />
        <span className={selectedDate ? '' : 'placeholder'}>
          {selectedDate ? jalaliDateLabel(selectedDate) : placeholder}
        </span>
      </button>
      {selectedDate && (
        <button
          type="button"
          className="dev-jalali-clear"
          onClick={() => {
            onChange('');
            setIsOpen(false);
          }}
          title="پاک کردن تاریخ"
          aria-label={`پاک کردن ${ariaLabel}`}
        >
          <ClearIcon fontSize="small" />
        </button>
      )}
      {calendar}
    </div>
  );
}
